import { api } from './api';

// API Base URL
import { API_BASE_URL } from '@/lib/apiConfig';

// Helper function for API requests with auth
async function apiRequest<T>(endpoint: string): Promise<T> {
  return api.get<T>(endpoint);
}

// Therapy Category Type
export type TherapyCategory = 'trt' | 'hrt' | 'glp1' | 'peptide' | 'nad' | 'fertility' | 'general';

// Missing field descriptor sent with targeted intake requests
export interface MissingFieldDescriptor {
  fieldId: string;
  fieldName: string;
  priority: string;
  category: string;
}

// Request types
export interface InitiateIntakeRequest {
  patientId: string;
  clinicId?: string;
  intakeType: 'targeted' | 'full';
  therapyCategory: string;
  missingFields?: MissingFieldDescriptor[];
  patientEmail?: string;
  patientName?: string;
  providerNotes?: string;
  sendEmail: boolean;
  sendToMdi?: boolean;
  // Optional appointment linkage. When supplied, the resulting PendingIntake
  // is bound to the appointment so the AppointmentSheet/visit cockpit can
  // surface intake status pre-visit and seed the SOAP Subjective from the
  // intake response.
  appointmentId?: string;
}

// Response types
export interface InitiateIntakeResponse {
  success: boolean;
  message: string;
  data: {
    intakeId: string;
    intakeLink: string;
    token: string;
    expiresAt: string;
    emailSent: boolean;
    emailError: string | null;
  };
}

export interface PendingIntake {
  id: string;
  token?: string;
  // Backend returns snake_case
  patient_id?: string;
  patient_name?: string;
  patient_email?: string;
  intake_type?: string;
  therapy_category?: TherapyCategory;
  status: 'pending' | 'started' | 'completed' | 'expired' | 'cancelled';
  created_at?: string;
  expires_at?: string;
  completed_at?: string;
  updated_at?: string;
  started_at?: string;
  response_id?: string;
  questionnaire_response_id?: string;
  is_reviewed?: boolean;
  reviewed_at?: string;
  appointment_id?: string;
  // Frontend may use camelCase
  patientId?: string;
  patientName?: string;
  patientEmail?: string;
  intakeType?: string;
  therapyCategory?: TherapyCategory;
  createdAt?: string;
  expiresAt?: string;
  completedAt?: string;
  responseId?: string;
  questionnaireResponseId?: string;
  isReviewed?: boolean;
  reviewedAt?: string;
  appointmentId?: string;
}

export interface PendingIntakesResponse {
  success: boolean;
  data: PendingIntake[];
  message: string;
}

export interface IntakeHistoryResponse {
  success: boolean;
  data: PendingIntake[];
  message: string;
}

export interface ResendIntakeResponse {
  success: boolean;
  message: string;
  data: {
    emailSent: boolean;
    emailError: string | null;
  };
}

export interface CancelIntakeResponse {
  success: boolean;
  message: string;
}

// Service object with api wrapper pattern
export const intakeService = {
  /**
   * Initiate a new intake request and optionally send email to patient
   */
  initiateIntake: (data: InitiateIntakeRequest): Promise<InitiateIntakeResponse> => {
    return api.post('/intakes/initiate', data);
  },

  /**
   * Resend intake email to patient
   */
  resendIntake: (intakeId: string): Promise<ResendIntakeResponse> => {
    return api.post(`/intakes/${intakeId}/resend`);
  },

  /**
   * Get all pending intakes for the current practitioner
   */
  getPendingIntakes: (): Promise<PendingIntakesResponse> => {
    return api.get('/intakes/pending');
  },

  /**
   * Get intake history for a specific patient
   */
  getPatientIntakeHistory: (patientId: string): Promise<IntakeHistoryResponse> => {
    return api.get(`/intakes/patient/${patientId}`);
  },

  /**
   * Get all intakes linked to a specific appointment.
   * Used by AppointmentSheet's intake-status row and the PreVisitPrepPanel.
   */
  getIntakesForAppointment: (
    appointmentId: string
  ): Promise<{ success: boolean; data: PendingIntake[]; message?: string }> => {
    return api.get(`/intakes/by-appointment/${appointmentId}`);
  },

  /**
   * Cancel a pending intake
   */
  cancelIntake: (intakeId: string): Promise<CancelIntakeResponse> => {
    return api.post(`/intakes/${intakeId}/cancel`);
  },
};

// ============================================================================
// STANDALONE FUNCTIONS (for direct import)
// ============================================================================

/**
 * Initiate a new patient intake (standalone function)
 */
export async function initiateIntake(
  data: InitiateIntakeRequest
): Promise<InitiateIntakeResponse> {
  return intakeService.initiateIntake(data);
}

/**
 * Resend intake email to patient (standalone function)
 */
export async function resendIntakeEmail(
  intakeId: string
): Promise<ResendIntakeResponse> {
  return intakeService.resendIntake(intakeId);
}

/**
 * Cancel a pending intake request (standalone function)
 */
export async function cancelIntakeRequest(
  intakeId: string
): Promise<CancelIntakeResponse> {
  return intakeService.cancelIntake(intakeId);
}

// ============================================================================
// DRAFT (AUTO-SAVE) FUNCTIONS
// ============================================================================

export interface SaveDraftRequest {
  draftData: Record<string, unknown>;
  currentStep: number;
}

export interface SaveDraftResponse {
  success: boolean;
  message?: string;
  data?: {
    savedAt: string;
    currentStep: number;
  };
  error?: string;
}

export interface GetDraftResponse {
  success: boolean;
  data?: {
    hasDraft: boolean;
    draftData: Record<string, unknown> | null;
    currentStep: number;
    lastSavedAt: string | null;
  };
  error?: string;
}

/**
 * Save intake draft (auto-save - public endpoint)
 * POST /api/intakes/draft/:token
 */
export async function saveIntakeDraft(
  token: string,
  data: SaveDraftRequest
): Promise<SaveDraftResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}intakes/draft/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: 'Failed to save draft',
        error: result.message || 'Save failed',
      };
    }
    
    return result;
  } catch (error) {
    console.error('Error saving draft:', error);
    return {
      success: false,
      message: 'Failed to save draft',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get intake draft (for resume - public endpoint)
 * GET /api/intakes/draft/:token
 */
export async function getIntakeDraft(
  token: string
): Promise<GetDraftResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}intakes/draft/${token}`);
    const result = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to get draft',
      };
    }
    
    return result;
  } catch (error) {
    console.error('Error getting draft:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// DASHBOARD & METRICS FUNCTIONS
// ============================================================================

export interface DashboardFilters {
  status?: string;
  therapyCategory?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  clinicId?: string;
}

export interface DashboardResponse {
  success: boolean;
  data?: {
    intakes: PendingIntake[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
  error?: string;
}

export interface MetricsResponse {
  success: boolean;
  data?: {
    total: number;
    completed: number;
    reviewed: number;
    pending: number;
    started: number;
    expired: number;
    cancelled: number;
    completionRate: number;
    avgCompletionMinutes: number;
    byCategory: Record<string, { total: number; completed: number }>;
    period: string;
  };
  error?: string;
}

export interface RecentCompletion {
  id: string;
  intakeId?: string;
  patientName: string;
  patientEmail: string;
  therapyCategory: TherapyCategory;
  intakeType: string;
  completedAt: string;
  scores: Record<string, number> | null;
}

export interface AnalyticsData {
  funnel: {
    sent: number;
    opened: number;
    started: number;
    completed: number;
    dropoffs: {
      notOpened: number;
      notStarted: number;
      abandoned: number;
    };
  };
  trends: Array<{
    week: string;
    sent: number;
    completed: number;
    rate: number;
  }>;
  timeDistribution: {
    under5min: number;
    '5to10min': number;
    '10to15min': number;
    '15to30min': number;
    over30min: number;
  };
  scoreDistributions: {
    adam: {
      positive: number;
      negative: number;
      average: number | null;
      distribution: Record<number, number>;
    };
    stopBang: {
      high: number;
      intermediate: number;
      low: number;
      average: number | null;
      distribution: Record<number, number>;
    };
  };
  byCategory: Record<string, {
    total: number;
    completed: number;
    avgCompletionMinutes: number;
    completionRate: number;
  }>;
  period: string;
  totalIntakes: number;
}

export interface AnalyticsResponse {
  success: boolean;
  data?: AnalyticsData;
  error?: string;
}

/**
 * Get dashboard data with filters
 * GET /api/intakes/dashboard
 */
export async function getDashboardData(
  filters: DashboardFilters = {}
): Promise<DashboardResponse> {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.therapyCategory) params.append('therapyCategory', filters.therapyCategory);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.clinicId) params.append('clinicId', filters.clinicId);

    const response = await api.get<DashboardResponse>(
      `intakes/dashboard?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching dashboard:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get intake metrics
 * GET /api/intakes/metrics
 */
export async function getIntakeMetrics(
  startDate: string,
  endDate: string,
  clinicId?: string
): Promise<MetricsResponse> {
  try {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (clinicId) params.append('clinicId', clinicId);
    
    const response = await api.get<MetricsResponse>(
      `intakes/metrics?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching metrics:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get recent completions for notifications
 * GET /api/intakes/recent-completions
 */
export async function getRecentCompletions(
  minutes: number = 60,
  clinicId?: string
): Promise<{ success: boolean; data?: RecentCompletion[]; error?: string }> {
  try {
    const params = new URLSearchParams();
    params.append('minutes', minutes.toString());
    if (clinicId) params.append('clinicId', clinicId);
    
    const response = await api.get<{ success: boolean; data: RecentCompletion[] }>(
      `intakes/recent-completions?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching recent completions:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get count of completed but unreviewed intakes
 * GET /api/intakes/unreviewed-count
 */
export async function getUnreviewedCount(
  clinicId?: string
): Promise<{
  success: boolean;
  data?: { count: number };
  error?: string;
}> {
  try {
    const params = new URLSearchParams();
    if (clinicId) params.append('clinicId', clinicId);
    
    const queryString = params.toString();
    const response = await api.get<{ success: boolean; data: { count: number } }>(
      `intakes/unreviewed-count${queryString ? `?${queryString}` : ''}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching unreviewed count:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get intake analytics
 * GET /api/intakes/analytics
 */
export async function getIntakeAnalytics(
  startDate: string,
  endDate: string,
  clinicId?: string
): Promise<AnalyticsResponse> {
  try {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (clinicId) params.append('clinicId', clinicId);

    const response = await api.get<AnalyticsResponse>(
      `intakes/analytics?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching analytics:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get therapy category label
 */
export function getTherapyCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    trt: "Men's Health (TRT)",
    hrt: "Women's Health (HRT)",
    glp1: 'Weight Management (GLP-1)',
    peptide: 'Peptide Therapy',
    nad: 'NAD+ Therapy',
    fertility: 'Fertility',
    general: 'General Health',
  };
  return labels[category] || category;
}

/**
 * Get intake type label
 */
export function getIntakeTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    targeted: 'Quick Form',
    full: 'Complete Profile',
    general: 'General Health',
    mens_health: "Men's Health",
    womens_health: "Women's Health",
    metabolic: 'Metabolic Health',
    pcos: 'PCOS Assessment',
  };
  return labels[type] || type;
}

/**
 * Get intake route based on type and therapy category
 */
export function getIntakeRoute(
  token: string,
  therapyCategory?: string
): string {
  // Route to specialized intakes based on therapy category
  if (therapyCategory === 'hrt') {
    return `/patient/intake/womens/${token}`;
  }
  if (therapyCategory === 'trt') {
    return `/patient/intake/mens/${token}`;
  }
  if (therapyCategory === 'glp1') {
    return `/patient/intake/weightloss/${token}`;
  }
  // Default to general intake
  return `/patient/intake/${token}`;
}

// ============================================================================
// RESPONSE LOOP FUNCTIONS
// ============================================================================

export interface IntakeStatusItem {
  id: string;
  intakeType: string;
  therapyCategory: string;
  intakePurpose?: string;
  intake_purpose?: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  completedAt?: string;
  providerName?: string;
  responseId?: string;
  questionnaireResponseId?: string;
  questionnaire_response_id?: string;
  scores?: Record<string, number>;
  riskFlags?: Record<string, boolean>;
  responseCompletedAt?: string;
  isReviewed?: boolean;
  isGuidedIntake?: boolean;
  reviewedAt?: string;
  reviewedBy?: string;
  /** Populated when the intake was created from a clinic-authored form builder template. */
  intakeFormTemplate?: { id: string; name: string } | null;
}

/**
 * Display label for a single intake row. Custom form-builder intakes show
 * "Custom · {name}"; built-in therapy intakes fall back to the category map.
 */
export function getIntakeDisplayLabel(intake: IntakeStatusItem): string {
  if (intake.intakeFormTemplate?.name) {
    return `Custom · ${intake.intakeFormTemplate.name}`;
  }
  return getTherapyCategoryLabel(intake.therapyCategory);
}

export interface PatientIntakeStatusResponse {
  success: boolean;
  data?: {
    pending: IntakeStatusItem[];
    completed: IntakeStatusItem[];
    reviewed: IntakeStatusItem[];
    summary: {
      pendingCount: number;
      newResponsesCount: number;
      reviewedCount: number;
    };
  };
  error?: string;
}

export interface ResponseDetailsResponse {
  success: boolean;
  data?: {
    id: string;
    intakeType: string;
    therapyCategory: string;
    responses: Array<{ questionId: string; value: unknown }>;
    scores: Record<string, number>;
    riskFlags?: Record<string, boolean>;
    completedAt: string;
    isReviewed: boolean;
    reviewedAt?: string;
    reviewNotes?: string;
    reviewedBy?: { id: string; name: string };
    intake?: {
      id: string;
      patientName: string;
      patientEmail: string;
      createdAt: string;
    };
    patient?: {
      id: string;
      name: string;
      dateOfBirth: string;
      gender: string;
    };
  };
  error?: string;
}

export interface MarkReviewedResponse {
  success: boolean;
  data?: {
    responseId: string;
    reviewedAt: string;
    reviewedBy: string;
  };
  error?: string;
}

/**
 * Get intake status for a patient (pending, completed, reviewed)
 */
export async function getPatientIntakeStatus(
  patientId: string
): Promise<PatientIntakeStatusResponse> {
  try {
    const response = await api.get<PatientIntakeStatusResponse>(
      `/intakes/patient/${patientId}/status`
    );
    return response;
  } catch (error) {
    console.error('Error fetching patient intake status:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get full response details for review
 */
export async function getResponseDetails(
  responseId: string
): Promise<ResponseDetailsResponse> {
  try {
    const response = await api.get<ResponseDetailsResponse>(
      `/intakes/responses/${responseId}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching response details:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Mark a response as reviewed by practitioner
 */
export async function markResponseAsReviewed(
  responseId: string,
  notes?: string
): Promise<MarkReviewedResponse> {
  try {
    const response = await api.post<MarkReviewedResponse>(
      `/intakes/responses/${responseId}/review`,
      { notes }
    );
    return response;
  } catch (error) {
    console.error('Error marking response as reviewed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// PREFILL DATA FUNCTIONS
// ============================================================================

/**
 * Prefill data interface - fields that can be prefilled from patient data
 */
export interface PrefillData {
  // Basic demographics
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  dob?: string;
  biological_sex?: string;
  gender?: string;
  
  // Address (legacy / primary)
  city?: string;
  state?: string;
  zip_code?: string;
  address_line_1?: string;
  address_line_2?: string;

  // Billing address
  billing_address_line_1?: string;
  billing_address_line_2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip_code?: string;

  // Shipping address
  shipping_address_line_1?: string;
  shipping_address_line_2?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_zip_code?: string;
  
  // Physical measurements
  height?: string;
  weight?: number;
  heightFeet?: number;
  heightInches?: number;
  
  // Medical history
  medications?: string;
  currentMedications?: string;
  allergies?: string;
  medical_history?: string;
  conditions?: string;
  medicalConditions?: string;
  chronicConditions?: string;
  surgeries?: string;
  family_history?: string;
  familyHistory?: string;
  
  // Lifestyle
  exercise_frequency?: string;
  exerciseFrequency?: string;
  activityLevel?: string;
  diet_quality?: string;
  diet?: string;
  sleep_hours?: string;
  sleepHours?: string;
  sleepQuality?: string;
  alcohol_frequency?: string;
  alcoholConsumption?: string;
  alcoholDrinks?: string;
  smoking_status?: string;
  tobacco_use?: string;
  smoking?: string;
  stress_level?: string;
  stressLevel?: string;
}

export interface PrefillDataResponse {
  success: boolean;
  data?: PrefillData;
  error?: string;
}

/**
 * Get prefill data for intake forms
 * GET /api/intakes/prefill/:token
 */
export async function getIntakePrefillData(
  token: string
): Promise<PrefillDataResponse> {
  try {
    // Uses module-level API_BASE_URL from apiConfig
    const response = await fetch(`${API_BASE_URL}intakes/prefill/${token}`);
    
    const result = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to get prefill data',
      };
    }
    
    return result;
  } catch (error) {
    console.error('Error getting prefill data:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Download PDF for a questionnaire response
 */
export async function downloadResponsePDF(responseId: string): Promise<void> {
  try {
    const token = localStorage.getItem('authToken');
    // Uses module-level API_BASE_URL from apiConfig
    
    const response = await fetch(`${API_BASE_URL}/intakes/responses/${responseId}/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }
    
    // Get the blob from response
    const blob = await response.blob();
    
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'intake-response.pdf';
    if (contentDisposition) {
      // Try to match filename="..." or filename=...
      const matches = contentDisposition.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i);
      if (matches && matches[1]) {
        filename = matches[1].trim();
      }
    }
    console.log('Downloading PDF with filename:', filename);
    
    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
}

export default intakeService;
